import React from 'react';
import { styled } from '@linaria/react';
import { animated, useSpring } from 'react-spring';

import { normalize } from '@/utils';

import Demo, { DemoSliderControl } from '@/components/Demo';
import Paper from '@/components/Paper';
import ToggleButton from '@/components/ToggleButton';

import { CODE_BACKGROUND } from './index.constants';
import Element from './Element';

const CODE = {
  initial: `\
.element {
  max-width: fit-content;
  margin-left: auto;
  margin-right: auto;
}`,
  left: `\
.element {
  max-width: fit-content;
  margin-left: auto;
}`,
  logical: `\
.element {
  max-width: fit-content;
  margin-inline: auto;
}`,
};

function AutoMargins({ codeOutput, variant }) {
  const [isMarginRevealed, setIsMarginRevealed] =
    React.useState(false);

  return (
    <Demo
      initialValues={{
        containerWidth: 100,
      }}
      controlPosition="top"
      customGrid={{ gridTemplateColumns: '1fr' }}
      outputWrapper={null}
      controls={[
        <DemoSliderControl
          key="containerWidth"
          id="containerWidth"
          label="Container Width"
          min={25}
          max={100}
          previewRenderer={(val) => `${val}%`}
        />,
      ]}
    >
      {({ containerWidth }) => {
        return (
          <div>
            <Row>
              <Column>{codeOutput}</Column>
              <DemoColumn>
                <Container style={{ width: containerWidth + '%' }}>
                  <MarginBoxes
                    variant={variant}
                    containerWidth={containerWidth}
                    isMarginRevealed={isMarginRevealed}
                  />

                  <MarginElement
                    style={{
                      marginLeft: variant !== 'logical' ? 'auto' : '',
                      marginRight:
                        variant === 'initial' ? 'auto' : '',
                      marginInline:
                        variant === 'logical' ? 'auto' : '',
                    }}
                  >
                    .element
                  </MarginElement>
                </Container>
              </DemoColumn>
            </Row>
            <Footer>
              <ToggleButton
                active={isMarginRevealed}
                handleToggle={() =>
                  setIsMarginRevealed(!isMarginRevealed)
                }
              >
                Reveal Margin
              </ToggleButton>
            </Footer>
          </div>
        );
      }}
    </Demo>
  );
}

function MarginBoxes({ variant, containerWidth, isMarginRevealed }) {
  // The spring settings should depend on the container width; essentially, the smaller the margin is, the quicker the animation should run.
  // We also need to lengthen the animation for the "left" variant, since there's only 1 margin which is 2x the size.
  const config = {
    tension: 200,
    friction:
      variant === 'left'
        ? normalize(containerWidth, 30, 100, 20, 40)
        : normalize(containerWidth, 30, 100, 10, 30),
  };
  const rightMarginStyles = useSpring({
    clipPath: isMarginRevealed
      ? `polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)`
      : `polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)`,
    config,
  });
  const leftMarginStyles = useSpring({
    clipPath: isMarginRevealed
      ? `polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)`
      : `polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)`,
    config,
  });

  return (
    <MarginWrapper>
      <Margin style={leftMarginStyles} />
      <Element style={{ opacity: 0 }}>.element</Element>
      {variant !== 'left' && <Margin style={rightMarginStyles} />}
    </MarginWrapper>
  );
}

const Row = styled.div`
  display: flex;
  gap: 16px;
  padding-bottom: 16px;

  @media (max-width: 35rem) {
    flex-direction: column-reverse;
    align-items: center;
  }
`;
const Column = styled.div`
  position: relative;
  flex: 1;
  background: ${CODE_BACKGROUND};
  border-radius: 8px;
  width: 100%;
  max-width: 335px;
`;

const DemoColumn = styled(Column)`
  display: flex;
  justify-content: center;
`;

const MarginWrapper = styled.div`
  position: absolute;
  top: 16px;
  width: 100%;
  height: 4rem;
  display: flex;
`;

const Margin = styled(animated.div)`
  flex: 1;
  background: hsl(30deg 70% 78%);
`;

const MarginElement = styled(Element)`
  background: white;
  height: 4rem;
`;

const Container = styled(Paper)`
  position: relative;
  min-height: 190px;
  padding: 16px 0px;
  /* Trim corners */
  overflow: hidden;
  overflow: clip;
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
`;

export default AutoMargins;
